//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { createContext } from 'react';
import {
  type AppConfig, type ConfigXgt, type ConfigXgtUpdate,
  type MemoryData, type PluginInfoPayload
} from '@/services/apiTypes';

// -----------------------------------------------------------------------
// Config context value.
//
// Holds the app/xgt/memory config state plus the config methods.  All three
// are backed by the TanStack Query cache (appConfigQueries, xgtQueries); the
// login page seeds appConfig through the setter here with the response it
// already had to read to offer the auth types.
// -----------------------------------------------------------------------

// What a caller may install: the config it just read, or a function of what
// is held. Narrower than a setState action -- nothing puts the config back
// to nothing, and every writer has a whole `config/app` response in hand.
export type AppConfigUpdate =
  AppConfig | ((previous: AppConfig | null) => AppConfig);

export interface ConfigContextValue {
  // --- State ---
  appConfig: AppConfig | null;
  setAppConfig: (update: AppConfigUpdate) => void;
  // configData and memoryData are read-only here (no setters; the cache is
  // populated by the queries in xgtQueries).
  configData: ConfigXgt | null;
  memoryData: MemoryData | null;

  // --- Methods ---
  getAppConfig: () => Promise<AppConfig>;
  // Re-reads config/app, for consumers that changed something the filtered
  // config derives from (an admin disabling a model, say). A call made while
  // a read is out supersedes it, so what ends up held was read after the
  // change that asked for it. Rejects if the read failed, a 401 included,
  // which the shared query error policy has already acted on.
  refreshAppConfig: () => Promise<void>;
  setConfigXgt: (newSettings: ConfigXgtUpdate) => Promise<void>;
  testODBC: (
    connectionString: string, index: number, modified: boolean
  ) => Promise<void>;
  fetchPluginInfo: (url: string) => Promise<PluginInfoPayload>;
}

const ConfigContext = createContext<ConfigContextValue | null>(null);

export { ConfigContext };
